import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Search, Plus, Star, Edit2, Tag } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PriceEditDialog } from '@/components/pricing/PriceEditDialog';
import { useTranslation } from 'react-i18next';

type PriceList = components['schemas']['PriceListResource'];
type Product = components['schemas']['ProductResource'];

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message) || fallback;
  }
  return fallback;
}

export default function PriceLists() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedListId, setSelectedListId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [search, setSearch] = useState('');
  const [editProductId, setEditProductId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const { data: listsData, isLoading } = useQuery({
    queryKey: ['/price-lists'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/price-lists');
      if (error) throw new Error(errorMessage(error, t('cat_pricing.loadListsError')));
      return data;
    },
  });
  const lists: PriceList[] = listsData?.data ?? [];

  const { data: productsData } = useQuery({
    queryKey: ['/products'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/products');
      if (error) throw new Error(errorMessage(error, t('cat_common.loadProductsError')));
      return data;
    },
  });
  const products: Product[] = productsData?.data ?? [];

  const { data: itemsData } = useQuery({
    queryKey: ['/price-lists/{priceList}/items', selectedListId],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/price-lists/{priceList}/items', {
        params: { path: { priceList: selectedListId! } },
      });
      if (error) throw new Error(errorMessage(error, t('cat_pricing.loadPricesError')));
      return data;
    },
    enabled: selectedListId !== null,
  });
  const items = itemsData?.data ?? [];

  const selectedList = lists.find(l => l.id === selectedListId);

  const invalidateLists = () => queryClient.invalidateQueries({ queryKey: ['/price-lists'] });
  const invalidateItems = () => queryClient.invalidateQueries({ queryKey: ['/price-lists/{priceList}/items', selectedListId] });

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.POST('/price-lists', { body: { name: newName.trim() } });
      if (error) throw new Error(errorMessage(error, t('cat_pricing.createError')));
    },
    onSuccess: () => {
      invalidateLists();
      setNewName('');
      setShowCreate(false);
      toast({ title: t('cat_pricing.created') });
    },
    onError: (err: Error) => toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' }),
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.PATCH('/price-lists/{priceList}', {
        params: { path: { priceList: id } },
        body: { is_default: true },
      });
      if (error) throw new Error(errorMessage(error, t('cat_common.error')));
    },
    onSuccess: () => {
      invalidateLists();
      toast({ title: t('cat_pricing.defaultChanged') });
    },
    onError: (err: Error) => toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' }),
  });

  const listItems = selectedList
    ? products.map((p) => {
        const item = items.find(i => i.product_id === p.id);
        return { product: p, item };
      }).filter(x => !search
        || (x.product.name ?? '').toLowerCase().includes(search.toLowerCase())
        || (x.product.sku ?? '').toLowerCase().includes(search.toLowerCase())
        || (x.product.barcode ?? '').toLowerCase().includes(search.toLowerCase()))
    : [];

  const editingRow = listItems.find(x => x.product.id === editProductId) || null;
  const editingOriginalPrice = editingRow?.item ? Number(editingRow.item.price) : 0;

  const handleSavePrices = async ({ price, sale_price }: { price: number; sale_price: number }) => {
    if (editProductId === null || selectedListId === null) return;
    setSaving(true);
    try {
      const { error: priceError } = await apiClient.PUT('/price-lists/{priceList}/items/{product}', {
        params: { path: { priceList: selectedListId, product: editProductId } },
        body: { price },
      });
      if (priceError) throw new Error(errorMessage(priceError, t('cat_pricing.savePriceError')));

      const { error: saleError } = await apiClient.PATCH('/products/{product}', {
        params: { path: { product: editProductId } },
        body: { sale_price },
      });
      if (saleError) throw new Error(errorMessage(saleError, t('cat_pricing.saveSaleError')));

      invalidateItems();
      queryClient.invalidateQueries({ queryKey: ['/products'] });
      toast({ title: t('cat_pricing.pricesSaved') });
      setEditProductId(null);
    } catch (err: any) {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold text-foreground">{t('cat_pricing.title')}</h1>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus size={14} className="mr-1" /> {t('cat_pricing.newList')}
        </Button>
      </div>

      {!selectedList && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? (
            <p className="text-muted-foreground col-span-3 text-center p-8">{t('cat_common.loading')}</p>
          ) : lists.length === 0 ? (
            <p className="text-muted-foreground col-span-3 text-center p-8">{t('cat_pricing.notFound')}</p>
          ) : (
            lists.map(list => (
              <div
                key={list.id}
                className="bg-card border border-border rounded-xl p-4 hover:shadow-md transition-shadow cursor-pointer group"
                onClick={() => setSelectedListId(list.id!)}
              >
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-medium text-foreground">{list.name}</h3>
                  {list.is_default ? (
                    <Badge className="bg-primary/10 text-primary border-0 text-xs">
                      <Star size={10} className="mr-1 fill-current" /> {t('cat_pricing.default')}
                    </Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100 h-7 text-xs text-muted-foreground"
                      onClick={e => { e.stopPropagation(); setDefaultMutation.mutate(list.id!); }}
                    >
                      {t('cat_pricing.makeDefault')}
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {selectedList && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedListId(null); setSearch(''); }}>
              {t('cat_pricing.back')}
            </Button>
            <h2 className="text-lg font-medium text-foreground">{selectedList.name}</h2>
            {selectedList.is_default && (
              <Badge className="bg-primary/10 text-primary border-0 text-xs">
                <Star size={10} className="mr-1 fill-current" /> {t('cat_pricing.default')}
              </Badge>
            )}
          </div>

          <div className="relative max-w-xs mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('cat_pricing.searchProduct')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>

          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.product')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.brand')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.category')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.unit')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_pricing.originalPrice')}</th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">
                      <span className="flex items-center gap-1"><Tag size={12} /> {t('cat_pricing.discountPrice')}</span>
                    </th>
                    <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.discount')}</th>
                    <th className="p-3 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {listItems.length === 0 ? (
                    <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">{t('cat_common.productNotFound')}</td></tr>
                  ) : listItems.map(({ product, item }) => {
                    const originalPrice = item ? Number(item.price) : 0;
                    const salePrice = Number(product.sale_price) || 0;
                    const discount = salePrice > 0 && originalPrice > salePrice ? originalPrice - salePrice : 0;

                    return (
                      <tr
                        key={product.id}
                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => setEditProductId(product.id!)}
                      >
                        <td className="p-3 text-sm text-muted-foreground font-mono">{product.sku}</td>
                        <td className="p-3 text-sm text-foreground font-medium">{product.name}</td>
                        <td className="p-3 text-sm text-muted-foreground">{product.brend_nomi || '—'}</td>
                        <td className="p-3 text-sm text-muted-foreground">{product.category}</td>
                        <td className="p-3 text-sm text-muted-foreground">{product.unit}</td>
                        <td className="p-3 text-sm">
                          <span className={item ? 'text-foreground' : 'text-muted-foreground italic'}>
                            {item ? formatUZS(originalPrice) : t('cat_pricing.notSet')}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          <span className={salePrice > 0 ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                            {salePrice > 0 ? formatUZS(salePrice) : '0'}
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          {discount > 0 ? (
                            <Badge variant="secondary" className="text-xs font-mono">
                              −{formatUZS(discount)}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">0</span>
                          )}
                        </td>
                        <td className="p-3">
                          <button
                            className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"
                            title={t('cat_pricing.editPrices')}
                            onClick={e => { e.stopPropagation(); setEditProductId(product.id!); }}
                          >
                            <Edit2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <PriceEditDialog
        open={editProductId !== null}
        onOpenChange={v => { if (!v) setEditProductId(null); }}
        product={editingRow?.product ?? null}
        originalPrice={editingOriginalPrice}
        onSave={handleSavePrices}
        saving={saving}
      />

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cat_pricing.newListTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Input placeholder={t('cat_pricing.listName')} value={newName} onChange={e => setNewName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t('cat_common.cancel')}</Button>
            <Button onClick={() => createMutation.mutate()} disabled={!newName.trim() || createMutation.isPending}>{t('cat_pricing.create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
